import React, { useEffect, useState } from 'react';
import { Modal } from 'reactstrap';
import { getCachedProgramTree } from "@/shared/helpers";
import { depositByAcceptBlue } from "@/services/program/transferMonies";
import { computeDepositTotal } from "@/services/program/computeDepositTotal";
import { assert3DS } from "@/services/program/assert3DS";
import { flashError, useDispatch } from '@/shared/components/flash';
import AcceptedCardBrands from '@/shared/components/AcceptedCardBrands';
import { useAcceptBluePayment } from "../hooks/useAcceptBluePayment";
import DepositPaymentResponseModal from "./DepositPaymentResponseModal";
import { depositResponseModalState } from "@/utils/depositHoldCheckout";

const getProgramFromTree = (pId, pTree) => {
  if (pTree?.length > 0) {
    for (const p of Object.values(pTree)) {
      if (p?.id && p.id == pId) return p;
      if (p?.children) {
        const found = getProgramFromTree(pId, p.children);
        if (found) return found;
      }
    }
  }
  return undefined;
};

const PaymentCreditCardStep_1 = ({ amount, isOpen, toggle, pId, orgId }) => {
  const dispatch = useDispatch();
  const ab = useAcceptBluePayment();
  const [loading, setLoading] = useState(false);
  const [program, setProgram] = useState(null);
  const [billing, setBilling] = useState({
    first_name: '', last_name: '', name_on_card: '', address: '', city: '', state: '', zip: '',
  });
  const [responseModal, setResponseModal] = useState({ open: false, status: null, errorMessage: null });

  useEffect(() => {
    if (pId) {
      const p = getProgramFromTree(pId, getCachedProgramTree());
      setProgram(p);
    }
  }, [pId]);

  useEffect(() => {
    if (isOpen) {
      ab.mountContainer('#accept-blue-container');
    } else {
      ab.destroy();
    }
    return () => {
      ab.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onBillingChange = (field) => (e) => {
    setBilling((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const isFormValid = billing.first_name.trim() && billing.last_name.trim();

  const closeResponseModal = () => {
    setResponseModal({ open: false, status: null, errorMessage: null });
    if (responseModal.status === 'processed' || responseModal.status === 'held') {
      toggle();
    }
  };

  const onClickMakePayment = async () => {
    if (!isFormValid) {
      flashError(dispatch, 'Please enter the cardholder first and last name.');
      return;
    }

    setLoading(true);
    try {
      const { nonce, expiryMonth, expiryYear } = await ab.getNonceToken();

      const total = computeDepositTotal(amount, program);
      const paayBilling = {
        line1: billing.address || '',
        city: billing.city || '',
        state: billing.state || '',
        postCode: billing.zip || '',
        country: '840',
      };

      const tds = await ab.verify3DS({ amount: total, billing: paayBilling });

      if (!assert3DS(tds)) {
        flashError(dispatch, '3-D Secure authentication failed. Please try another card.');
        return;
      }

      const payload = {
        token: `nonce-${nonce}`,
        amount,
        expiry_month: parseInt(expiryMonth, 10),
        expiry_year: String(expiryYear).length === 2 ? 2000 + parseInt(expiryYear, 10) : parseInt(expiryYear, 10),
        first_name: billing.first_name,
        last_name: billing.last_name,
        name_on_card: billing.name_on_card || undefined,
        address: billing.address || undefined,
        city: billing.city || undefined,
        state: billing.state || undefined,
        zip: billing.zip || undefined,
        application_type: 'FE',
        '3d_secure': { eci: tds.eci, cavv: tds.authenticationValue, ds_trans_id: tds.dsTransId },
      };

      const resp = await depositByAcceptBlue(orgId, pId, payload);

      const { status, message } = depositResponseModalState(resp);
      setResponseModal({ open: true, status, errorMessage: message });
      if (status === 'error') {
        flashError(dispatch, message || 'Your payment was canceled or failed.');
      }
    } catch (error) {
      flashError(dispatch, error?.message || error);
    } finally {
      setLoading(false);
    }
  };

  if (!program || !amount) return null;

  const total = computeDepositTotal(amount, program);
  const fmt = (n) => `$${parseFloat(n).toFixed(2)}`;

  return (
    <>
      <Modal className="cc-confirm-modal" isOpen={isOpen} toggle={toggle}>
        <div className="cc-confirm-modal__inner">

          <div className="cc-confirm-modal__header">
            <div className="cc-confirm-modal__header-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M6 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <h3 className="cc-confirm-modal__title">Confirm Payment</h3>
              <p className="cc-confirm-modal__subtitle">Review charges and enter your card details</p>
            </div>
            <button className="cc-confirm-modal__close" onClick={toggle} aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="cc-confirm-modal__body">
            <p className="cc-confirm-modal__note">
              Enter your card details below. Your card is charged securely; the program balance updates once payment is confirmed.
            </p>

            <div className="cc-confirm-modal__breakdown">
              <div className="cc-confirm-modal__row">
                <span>Deposit Amount</span>
                <span>{fmt(amount)}</span>
              </div>
              <div className="cc-confirm-modal__row cc-confirm-modal__row--total">
                <span>Total charged to card</span>
                <strong>{fmt(total)}</strong>
              </div>
            </div>

            <AcceptedCardBrands />
            <p className="cc-confirm-modal__note cc-confirm-modal__note--warning">
              Mastercard is not supported. Please use a different card to continue.
            </p>

            <div className="cc-confirm-modal__card-box" style={{ position: 'relative' }}>
              <div id="accept-blue-container"></div>
              {!ab.ready && (
                <div className="cc-confirm-modal__card-loading">Loading secure card entry…</div>
              )}
              {ab.challengeVisible && (
                <div className="cc-confirm-modal__card-loading">Verifying your card with your bank…</div>
              )}
            </div>

            <div className="cc-confirm-modal__billing-fields">
              <input
                type="text"
                placeholder="First Name"
                value={billing.first_name}
                onChange={onBillingChange('first_name')}
              />
              <input
                type="text"
                placeholder="Last Name"
                value={billing.last_name}
                onChange={onBillingChange('last_name')}
              />
              <input
                type="text"
                placeholder="Name on Card"
                value={billing.name_on_card}
                onChange={onBillingChange('name_on_card')}
              />
              <input
                type="text"
                placeholder="Address"
                value={billing.address}
                onChange={onBillingChange('address')}
              />
              <input
                type="text"
                placeholder="City"
                value={billing.city}
                onChange={onBillingChange('city')}
              />
              <input
                type="text"
                placeholder="State"
                value={billing.state}
                onChange={onBillingChange('state')}
              />
              <input
                type="text"
                placeholder="Zip"
                value={billing.zip}
                onChange={onBillingChange('zip')}
              />
            </div>
          </div>

          <div className="cc-confirm-modal__footer">
            <button
              type="button"
              className="cc-confirm-modal__btn cc-confirm-modal__btn--cancel"
              onClick={toggle}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cc-confirm-modal__btn cc-confirm-modal__btn--confirm"
              onClick={onClickMakePayment}
              disabled={loading || !ab.ready || !isFormValid}
            >
              {loading ? "Processing…" : "Make Payment"}
            </button>
          </div>

        </div>
      </Modal>

      <DepositPaymentResponseModal
        isOpen={responseModal.open}
        setOpen={closeResponseModal}
        status={responseModal.status}
        errorMessage={responseModal.errorMessage}
      />
    </>
  );
};

export default PaymentCreditCardStep_1;
